/**
 * AgentConversationListItem
 *
 * Individual conversation item in list with inline actions (Gmail pattern)
 */

import React, { useState, useRef, useEffect, useId } from 'react';
import { Tooltip } from 'react-tooltip';
import IconButton, { ICON_BUTTON_ICONS } from '../../buttons/IconButton';
import { AGENT_ICONS } from '../sidebar/AgentSidebarMenuEntry.jsx';
import pubSub from '../../../utils/pubsub';

const AgentConversationListItem = ({
  conversation,
  onClick = () => {},
  onFavorite = () => {},
  onArchive = () => {},
  onDelete = () => {},
  onRename = () => {},
  isSelected = false,
  onSelectionChange = () => {},
  chatUrl = '' // URL for "Open in new tab" right-click menu
}) => {
  const itemId = useId();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(conversation.title);
  const [isLinkContextMenuOpen, setIsLinkContextMenuOpen] = useState(false);
  const inputRef = useRef(null);
  const savingRef = useRef(false);

  // Close link context menu on click outside or when another menu opens
  useEffect(() => {
    const handleClickOutside = () => {
      if (isLinkContextMenuOpen) {
        setIsLinkContextMenuOpen(false);
      }
    };

    const handlePubSubContextClick = (payload) => {
      if (
        payload?.event === 'conversation-list-link-context-menu-trigger' &&
        isLinkContextMenuOpen
      ) {
        setIsLinkContextMenuOpen(false);
      }
    };

    pubSub.subscribe(handlePubSubContextClick);
    document.addEventListener('click', handleClickOutside);

    return () => {
      pubSub.unsubscribe(handlePubSubContextClick);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isLinkContextMenuOpen]);

  const handleRightClick = (e) => {
    if (!chatUrl) return; // No URL, no right-click menu

    e.preventDefault();
    e.stopPropagation();
    pubSub.publish({ event: 'conversation-list-link-context-menu-trigger' });
    setIsLinkContextMenuOpen((prev) => !prev);
  };

  const handleOpenInNewTab = () => {
    if (chatUrl) {
      window.open(chatUrl, '_blank');
      setIsLinkContextMenuOpen(false);
    }
  };

  // Focus and select all text when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Update editValue when conversation title changes externally
  useEffect(() => {
    setEditValue(conversation.title);
  }, [conversation.title]);
  const formatRelativeTime = (timestamp) => {
    // Handle undefined or invalid timestamps
    if (!timestamp) return 'Unknown';

    // Convert to Date object if it's a string
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'Unknown';

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;

    // For older dates, show formatted date
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const handleClick = () => {
    if (!isEditing) {
      onClick(conversation.id);
    }
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation(); // Prevent item click
    onSelectionChange(conversation.id, e.target.checked);
  };

  const handleRenameClick = (e) => {
    if (e) e.stopPropagation(); // Prevent item click
    setEditValue(conversation.title);
    setIsEditing(true);
  };

  const handleSaveRename = async () => {
    if (savingRef.current) return; // Prevent concurrent saves
    savingRef.current = true;

    const newValue = editValue.trim();
    const oldValue = conversation.title;

    // Early exit if no change or empty value
    if (!newValue || newValue === oldValue) {
      setIsEditing(false);
      savingRef.current = false;
      return;
    }

    // Optimistic update: exit edit mode immediately (non-blocking)
    setIsEditing(false);

    try {
      // Background API call
      await onRename(conversation.id, newValue);
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      // Rollback: re-enter edit mode with old value so user can retry
      setEditValue(oldValue);
      setIsEditing(true);
    } finally {
      savingRef.current = false; // Always reset, even on error
    }
  };

  const handleCancelRename = () => {
    setEditValue(conversation.title);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelRename();
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation(); // Prevent item click when clicking input
  };

  const handleFavorite = (e) => {
    if (e) e.stopPropagation(); // Prevent item click
    onFavorite(conversation.id);
  };

  const handleArchive = (e) => {
    if (e) e.stopPropagation(); // Prevent item click
    onArchive(conversation.id);
  };

  const handleDelete = (e) => {
    if (e) e.stopPropagation(); // Prevent item click
    onDelete(conversation.id);
  };

  return (
    <>
      <div
        id={itemId}
        className="agent-conversation-list-item"
        onClick={handleClick}
        onContextMenu={handleRightClick}
      >
        {/* Always-visible checkbox (Gmail pattern) */}
        <div className="agent-conversation-list-item-checkbox">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        <div className="agent-conversation-list-item-content">
          <div className="agent-conversation-list-item-header">
            <div className="agent-conversation-list-item-title">
              {isEditing ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onBlur={handleSaveRename}
                  onClick={handleInputClick}
                  className="agent-conversation-list-item-title-input"
                />
              ) : (
                conversation.title
              )}
            </div>
            <div className="agent-conversation-list-item-time">
              {formatRelativeTime(conversation.lastActivity)}
            </div>
          </div>
        </div>

        {/* Action buttons using IconButton (matching message action pattern) */}
        <div className="agent-conversation-list-item-actions">
          <IconButton
            icon={ICON_BUTTON_ICONS.PEN}
            tooltipText="Rename"
            tooltipPosition="bottom"
            withFill={false}
            withBorder={false}
            onClick={handleRenameClick}
          />
          <IconButton
            icon={conversation.isFavorite ? AGENT_ICONS.FAVORITE : ICON_BUTTON_ICONS.BOOKMARK}
            tooltipText={conversation.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            tooltipPosition="bottom"
            withFill={false}
            withBorder={false}
            onClick={handleFavorite}
          />
          <IconButton
            icon={ICON_BUTTON_ICONS.ARCHIVE}
            tooltipText={conversation.isArchived ? 'Unarchive' : 'Archive'}
            tooltipPosition="bottom"
            withFill={false}
            withBorder={false}
            onClick={handleArchive}
          />
          <IconButton
            icon={ICON_BUTTON_ICONS.TRASH}
            tooltipText="Delete"
            tooltipPosition="bottom"
            withFill={false}
            withBorder={false}
            onClick={handleDelete}
          />
        </div>
      </div>

      {/* Right-click "Open in new tab" tooltip menu */}
      {chatUrl && isLinkContextMenuOpen && (
        <Tooltip
          anchorSelect={`#${CSS.escape(itemId)}`}
          multiline={true}
          variant="dark"
          opacity={1}
          place="bottom"
          clickable={true}
          isOpen={true}
          className="icon-button-tooltip-context-link-menu"
        >
          <div className="context-link-menu-item" onClick={handleOpenInNewTab}>
            <div className="left-side">
              <div className="context-link-icon-wrap">
                <img src={ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT} alt="" />
              </div>
              <div className="context-link-text">Open in new tab</div>
            </div>
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default AgentConversationListItem;
